/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../../page-modules/account/services/account-data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { FormsHelper } from '../../../shared/helpers/forms-helper';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Organization } from '../../../shared/models/organization';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  templateUrl: './email-recovery.component.html',
  styleUrls: ['./email-recovery.component.scss'],
})
export class EmailRecoveryComponent implements OnInit {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  emailForm: FormGroup;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  showMaintenanceOverlay: boolean;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private translocoService: TranslocoService,
    private captchaV3Service: ReCaptchaV3Service,
    private cdr: ChangeDetectorRef,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
  }

  ngOnInit() {
    this.emailForm = this.fb.group(
      {
        email: ['', Validators.compose([Validators.required, Validators.email])],
        newEmail: ['', Validators.compose([Validators.required, Validators.email])],
      },
      {
        validator: FormsHelper.isEqual('email', 'newEmail'),
      },
    );
  }

  updateEmail() {
    if (this.emailForm.valid) {
      this.captchaElem.execute();
    }
  }

  proceedIfValid(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;
    this.proceedEmailRecovery(captchaResponseV2);
  }

  private proceedEmailRecovery(captchaResponseV2: string) {
    const newEmail = this.emailForm.get('email').value?.trim();
    const token = this.activatedRoute.snapshot.queryParamMap.get('token');
    this.captchaV3Service.execute('emailRecovery').subscribe((captchaResponseV3: string) => {
      this.accountService.updateUserEmail(newEmail, token, captchaResponseV3, captchaResponseV2).subscribe(({ isSuccess, error }) => {
        if (isSuccess) {
          RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
          SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, marker('translations.emailUpdated'));
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
        this.captchaElem.reset();
        this.cdr.detectChanges();
      });
    });
  }
}
